/* ==========================================================================
   Royal Honey — core
   Utilities · age gate · header & menus · instant search · cart panel & lines ·
   add to cart · quick view · wishlist · recently viewed · rails · tabs ·
   dialogs · countdown · popups · WhatsApp · misc
   ========================================================================== */
(() => {
  'use strict';

  const N = (window.SR = Object.assign({ routes: {}, settings: {}, strings: {} }, window.SR || {}));
  const root = document.documentElement;
  const reduceMotion = window.matchMedia('(prefers-reduced-motion: reduce)');

  /* ---------- Utilities ---------- */
  const define = (name, cls) => { if (!customElements.get(name)) customElements.define(name, cls); };
  const debounce = (fn, wait = 250) => { let t; return (...args) => { clearTimeout(t); t = setTimeout(() => fn(...args), wait); }; };
  const parse = (html) => new DOMParser().parseFromString(html, 'text/html');
  const focusables = (el) => [...el.querySelectorAll('a[href], button:not([disabled]), input:not([disabled]):not([type="hidden"]), select:not([disabled]), textarea:not([disabled]), [tabindex]:not([tabindex="-1"])')].filter((n) => n.offsetParent !== null || n === document.activeElement);
  const trapFocus = (event, container) => {
    if (event.key !== 'Tab' || !container) return;
    const items = focusables(container);
    if (!items.length) return;
    const first = items[0];
    const last = items[items.length - 1];
    if (event.shiftKey && document.activeElement === first) { event.preventDefault(); last.focus(); }
    else if (!event.shiftKey && document.activeElement === last) { event.preventDefault(); first.focus(); }
  };
  const toast = (message) => {
    const el = document.querySelector('[data-toast]');
    if (!el || !message) return;
    el.textContent = message;
    el.hidden = false;
    clearTimeout(el._timer);
    el._timer = setTimeout(() => { el.hidden = true; }, 3200);
  };
  const bus = {
    emit: (name, detail) => document.dispatchEvent(new CustomEvent(`sr:${name}`, { detail })),
    on: (name, fn) => document.addEventListener(`sr:${name}`, (e) => fn(e.detail))
  };
  const store = {
    get(key, fallback) { try { const v = window.localStorage.getItem(key); return v === null ? fallback : JSON.parse(v); } catch (err) { return fallback; } },
    set(key, value) { try { window.localStorage.setItem(key, JSON.stringify(value)); } catch (err) { /* private mode */ } }
  };
  const ageLocked = () => root.classList.contains('age-locked');
  const afterAge = (fn) => { if (ageLocked()) document.addEventListener('sr:age-ok', () => fn(), { once: true }); else fn(); };
  N.utils = { define, debounce, parse, focusables, trapFocus, toast, bus, store, reduceMotion, afterAge };

  /* ---------- Age gate ---------- */
  define('age-gate', class extends HTMLElement {
    connectedCallback() {
      if (!ageLocked()) return;
      this.yes = this.querySelector('[data-age-yes]');
      this.yes?.addEventListener('click', () => this.accept());
      this.form = this.querySelector('[data-age-form]');
      if (this.form) this.setupDate();
      this.addEventListener('keydown', (e) => trapFocus(e, this.querySelector('.agegate__panel')));
      requestAnimationFrame(() => (this.yes || this.form?.querySelector('input'))?.focus({ preventScroll: true }));
    }
    // Birth date mode: checked on the device only, nothing is sent or stored except the result.
    setupDate() {
      const parts = [...this.form.querySelectorAll('[data-age-part]')];
      const error = this.form.querySelector('[data-age-error]');
      parts.forEach((input, i) => input.addEventListener('input', () => {
        input.value = input.value.replace(/\D/g, '');
        if (input.value.length >= input.maxLength && parts[i + 1]) parts[i + 1].focus();
      }));
      this.form.addEventListener('submit', (e) => {
        e.preventDefault();
        const [d, m, y] = parts.map((p) => parseInt(p.value, 10));
        const date = new Date(y, m - 1, d);
        const now = new Date();
        const valid = y > 1900 && date.getFullYear() === y && date.getMonth() === m - 1 && date.getDate() === d && date <= now;
        const show = (msg) => { error.textContent = msg; error.hidden = false; };
        if (!valid) { show(error.dataset.invalid); parts.find((p) => !p.value)?.focus(); return; }
        let age = now.getFullYear() - y;
        if (now.getMonth() < m - 1 || (now.getMonth() === m - 1 && now.getDate() < d)) age -= 1;
        if (age < parseInt(this.dataset.min || '18', 10)) { show(error.dataset.refused); this.querySelector('[data-age-no]')?.focus(); return; }
        this.accept();
      });
    }
    accept() {
      try { window.localStorage.setItem('sr-age-ok', String(Date.now())); } catch (err) { /* private mode */ }
      root.classList.add('age-leaving');
      setTimeout(() => {
        root.classList.remove('age-locked', 'age-leaving');
        bus.emit('age-ok');
      }, reduceMotion.matches ? 0 : 340);
    }
  });

  /* ---------- Header ---------- */
  define('site-header', class extends HTMLElement {
    connectedCallback() {
      this.sheet = this.querySelector('[data-search-sheet]');
      this.measure = () => {
        this.fitNav();
        root.style.setProperty('--header-h', `${this.querySelector('.header__bar')?.offsetHeight || this.offsetHeight}px`);
        const bar = document.querySelector('.section-announcement');
        root.style.setProperty('--announce-h', `${bar ? bar.offsetHeight : 0}px`);
      };
      this.measure();
      document.fonts?.ready.then(this.measure);
      window.addEventListener('resize', debounce(this.measure, 150));
      this.onScroll = () => this.classList.toggle('is-scrolled', window.scrollY > 8);
      window.addEventListener('scroll', this.onScroll, { passive: true });
      this.onScroll();

      const fine = window.matchMedia('(hover: hover) and (pointer: fine)');
      this.querySelectorAll('[data-hover-menu]').forEach((menu) => {
        let timer;
        menu.addEventListener('mouseenter', () => { if (!fine.matches) return; clearTimeout(timer); this.closeMenus(menu); menu.open = true; });
        menu.addEventListener('mouseleave', () => { if (!fine.matches) return; timer = setTimeout(() => { menu.open = false; }, 180); });
        menu.addEventListener('toggle', () => this.syncOpen());
        menu.addEventListener('focusout', (e) => { if (!menu.contains(e.relatedTarget)) menu.open = false; });
      });

      this.drawer = this.querySelector('[data-mobile-menu]');
      this.drawer?.addEventListener('toggle', () => {
        document.body.classList.toggle('menu-open', this.drawer.open);
        this.syncOpen();
        if (this.drawer.open) requestAnimationFrame(() => this.drawer.querySelector('.menu-drawer__panel a, .menu-drawer__panel summary')?.focus({ preventScroll: true }));
      });
      this.querySelectorAll('[data-menu-close]').forEach((btn) => btn.addEventListener('click', () => { if (this.drawer) { this.drawer.open = false; this.drawer.querySelector('summary')?.focus(); } }));
      this.drawer?.addEventListener('keydown', (e) => trapFocus(e, this.drawer.querySelector('.menu-drawer__panel')));

      this.addEventListener('keydown', (e) => {
        if (e.key !== 'Escape') return;
        const open = this.querySelector('details[open]');
        if (open) { open.open = false; open.querySelector('summary')?.focus(); }
      });
      document.addEventListener('click', (e) => { if (!this.contains(e.target)) this.closeMenus(); });

      this.querySelectorAll('[data-search-open]').forEach((btn) => btn.addEventListener('click', (e) => { e.preventDefault(); this.openSearch(btn); }));
      this.sheet?.querySelectorAll('[data-search-close]').forEach((btn) => btn.addEventListener('click', () => this.closeSearch()));
      this.sheet?.addEventListener('keydown', (e) => {
        if (e.key === 'Escape') { e.stopPropagation(); this.closeSearch(); }
        trapFocus(e, this.sheet.querySelector('.search-sheet__panel'));
      });
    }
    disconnectedCallback() { window.removeEventListener('scroll', this.onScroll); }
    // A long menu never runs into the logo: when it does not fit, the header switches to the burger menu.
    fitNav() {
      const nav = this.querySelector('.header__links');
      this.classList.remove('is-nav-tight');
      if (!nav || !nav.offsetParent) return;
      const logo = this.querySelector('.header__logo');
      const tools = this.querySelector('.header__tools');
      const end = this.classList.contains('header--logo-left') ? tools : logo;
      if (!end) return;
      const navBox = nav.getBoundingClientRect();
      const endBox = end.getBoundingClientRect();
      const last = nav.lastElementChild?.getBoundingClientRect();
      const right = Math.max(navBox.right, last ? last.right : 0);
      if (right > endBox.left - 20 || nav.scrollWidth > nav.clientWidth + 1) this.classList.add('is-nav-tight');
    }
    syncOpen() { this.classList.toggle('is-open', Boolean(this.querySelector('details[open]'))); }
    closeMenus(except) { this.querySelectorAll('details[open]').forEach((d) => { if (d !== except && !d.hasAttribute('data-mobile-menu')) d.open = false; }); }
    openSearch(trigger) {
      if (!this.sheet) return;
      this.trigger = trigger;
      this.closeMenus();
      if (this.drawer) this.drawer.open = false;
      this.sheet.hidden = false;
      trigger?.setAttribute('aria-expanded', 'true');
      document.body.classList.add('search-open');
      requestAnimationFrame(() => {
        this.sheet.classList.add('is-open');
        this.sheet.querySelector('input[type="search"]')?.focus();
      });
    }
    closeSearch() {
      if (!this.sheet || this.sheet.hidden) return;
      this.sheet.classList.remove('is-open');
      document.body.classList.remove('search-open');
      this.trigger?.setAttribute('aria-expanded', 'false');
      setTimeout(() => { this.sheet.hidden = true; }, 300);
      this.trigger?.focus();
    }
  });

  /* ---------- Instant search ---------- */
  define('predictive-search', class extends HTMLElement {
    connectedCallback() {
      this.input = this.querySelector('input[type="search"]');
      this.results = this.querySelector('[data-search-results]');
      if (!this.input || !this.results) return;
      this.initial = this.results.innerHTML;
      this.enabled = this.dataset.enabled === 'true' && Boolean(N.routes.predictiveSearch);
      this.cache = new Map();
      this.input.addEventListener('input', debounce(() => this.search(), 220));
      this.addEventListener('keydown', (e) => this.navigate(e));
    }
    async search() {
      const q = this.input.value.trim();
      if (!q) { this.results.innerHTML = this.initial; return; }
      if (!this.enabled) return;
      if (this.cache.has(q)) { this.results.innerHTML = this.cache.get(q); return; }
      this.controller?.abort();
      this.controller = new AbortController();
      const params = new URLSearchParams({ q, section_id: 'predictive-search', 'resources[type]': 'product,collection,query,page,article', 'resources[limit]': '6', 'resources[limit_scope]': 'each' });
      try {
        const res = await fetch(`${N.routes.predictiveSearch}?${params}`, { signal: this.controller.signal });
        if (!res.ok) throw new Error(String(res.status));
        const html = parse(await res.text()).querySelector('[data-predictive-results]')?.outerHTML || '';
        this.cache.set(q, html || this.initial);
        if (this.input.value.trim() === q) this.results.innerHTML = html || this.initial;
      } catch (err) {
        if (err.name !== 'AbortError') this.results.innerHTML = this.initial;
      }
    }
    navigate(e) {
      if (e.key !== 'ArrowDown' && e.key !== 'ArrowUp') return;
      const links = [...this.results.querySelectorAll('a')];
      if (!links.length) return;
      e.preventDefault();
      const i = links.indexOf(document.activeElement);
      const next = e.key === 'ArrowDown' ? Math.min(i + 1, links.length - 1) : i - 1;
      (next < 0 ? this.input : links[next]).focus();
    }
  });

  /* ---------- Cart ---------- */
  const setCount = (count) => {
    document.querySelectorAll('[data-cart-count]').forEach((el) => { el.textContent = count; el.hidden = !count; });
  };
  const cartSections = () => {
    const ids = [];
    const drawer = document.querySelector('cart-drawer');
    if (drawer?.sectionId) ids.push(drawer.sectionId);
    document.querySelectorAll('[data-cart-section]').forEach((el) => { if (!ids.includes(el.dataset.sectionId)) ids.push(el.dataset.sectionId); });
    return ids;
  };
  const renderCart = (sections) => {
    const drawer = document.querySelector('cart-drawer');
    Object.entries(sections || {}).forEach(([id, html]) => {
      if (!html) return;
      const next = parse(html).querySelector('[data-cart-inner]');
      const host = drawer && id === drawer.sectionId ? drawer : document.querySelector(`[data-section-id="${id}"]`);
      const current = host?.querySelector('[data-cart-inner]');
      if (next && current) {
        current.replaceWith(next);
        setCount(parseInt(next.dataset.count, 10) || 0);
      }
    });
  };
  const cartPost = async (url, payload) => {
    const sections = cartSections();
    const res = await fetch(url, {
      method: 'POST',
      headers: { 'Content-Type': 'application/json', Accept: 'application/json' },
      body: JSON.stringify(Object.assign(payload, { sections, sections_url: window.location.pathname }))
    });
    const data = await res.json();
    if (!res.ok || data.status) throw new Error(data.description || data.message || N.strings.error);
    renderCart(data.sections);
    if (typeof data.item_count === 'number') setCount(data.item_count);
    bus.emit('cart:updated', data);
    return data;
  };
  N.cart = { renderCart, cartPost, setCount };

  define('cart-drawer', class extends HTMLElement {
    connectedCallback() {
      this.sectionId = this.dataset.sectionId;
      this.panel = this.querySelector('.drawer__panel');
      this.addEventListener('click', (e) => { if (e.target.closest('[data-drawer-close]')) this.close(); });
      this.addEventListener('keydown', (e) => { if (e.key === 'Escape') this.close(); trapFocus(e, this.panel); });
      document.addEventListener('click', (e) => {
        const trigger = e.target.closest('[data-cart-open]');
        if (!trigger) return;
        e.preventDefault();
        this.open(trigger);
      });
    }
    open(opener) {
      this.opener = opener || document.activeElement;
      this.classList.add('is-open');
      this.setAttribute('aria-hidden', 'false');
      document.body.classList.add('drawer-open');
      requestAnimationFrame(() => this.panel?.focus());
    }
    close() {
      if (!this.classList.contains('is-open')) return;
      this.classList.remove('is-open');
      this.setAttribute('aria-hidden', 'true');
      document.body.classList.remove('drawer-open');
      this.opener?.focus?.();
    }
    render(sections) { renderCart(sections); }
  });

  define('cart-items', class extends HTMLElement {
    connectedCallback() {
      const onChange = debounce((input) => this.update(input.dataset.line, input.value), 320);
      this.addEventListener('change', (e) => { if (e.target.matches('input[data-line]')) onChange(e.target); });
      this.addEventListener('click', (e) => {
        const remove = e.target.closest('[data-remove-line]');
        if (!remove) return;
        e.preventDefault();
        this.update(remove.dataset.removeLine, 0);
      });
    }
    async update(line, quantity) {
      this.classList.add('is-loading');
      try {
        await cartPost(`${N.routes.cartChange}.js`, { line: parseInt(line, 10), quantity: parseInt(quantity, 10) });
      } catch (err) {
        toast(err.message || N.strings.error);
        this.classList.remove('is-loading');
      }
    }
  });

  document.addEventListener('change', async (e) => {
    const gift = e.target.closest?.('[data-gift-toggle]');
    if (!gift) return;
    gift.disabled = true;
    try {
      if (gift.checked) await cartPost(`${N.routes.cartAdd}.js`, { items: [{ id: parseInt(gift.value, 10), quantity: 1 }] });
      else await cartPost(`${N.routes.cartChange}.js`, { id: String(gift.value), quantity: 0 });
    } catch (err) {
      toast(err.message || N.strings.error);
      gift.checked = !gift.checked;
      gift.disabled = false;
    }
  });
  const noteSave = debounce((value) => {
    fetch(`${N.routes.cartUpdate}.js`, { method: 'POST', headers: { 'Content-Type': 'application/json', Accept: 'application/json' }, body: JSON.stringify({ note: value }) });
  }, 500);
  document.addEventListener('input', (e) => { if (e.target.matches?.('[data-cart-note]')) noteSave(e.target.value); });

  /* ---------- Add to cart ---------- */
  define('product-form', class extends HTMLElement {
    connectedCallback() {
      this.form = this.querySelector('form');
      this.form?.addEventListener('submit', (e) => this.submit(e));
    }
    async submit(e) {
      const drawer = document.querySelector('cart-drawer');
      if (N.settings.cartType !== 'drawer' || !drawer) return;
      e.preventDefault();
      const button = e.submitter || this.form.querySelector('[data-add]');
      if (!button || button.disabled || button.classList.contains('is-loading')) return;
      const error = this.querySelector('[data-error]');
      button.classList.add('is-loading');
      button.setAttribute('aria-busy', 'true');
      if (error) error.hidden = true;
      const body = new FormData(this.form);
      if (button.name === 'id' && button.value) body.set('id', button.value);
      body.append('sections', cartSections().join(','));
      body.append('sections_url', window.location.pathname);
      try {
        const res = await fetch(`${N.routes.cartAdd}.js`, { method: 'POST', headers: { Accept: 'application/json', 'X-Requested-With': 'XMLHttpRequest' }, body });
        const data = await res.json();
        if (!res.ok || data.status) {
          const message = (typeof data.description === 'string' && data.description) || data.message || N.strings.error;
          bus.emit('cart:error', { form: this.form, data });
          if (error && !error.classList.contains('visually-hidden')) { error.textContent = message; error.hidden = false; }
          else toast(message);
          return;
        }
        renderCart(data.sections);
        this.closest('dialog')?.close();
        drawer.open(button);
        bus.emit('cart:added', data);
      } catch (err) {
        console.error(err);
        toast(N.strings.error);
      } finally {
        button.classList.remove('is-loading');
        button.removeAttribute('aria-busy');
      }
    }
  });

  /* ---------- Quantity ---------- */
  define('quantity-input', class extends HTMLElement {
    connectedCallback() {
      this.input = this.querySelector('input');
      this.querySelectorAll('button').forEach((btn) => btn.addEventListener('click', (e) => {
        e.preventDefault();
        const before = this.input.value;
        if (btn.name === 'plus') this.input.stepUp(); else this.input.stepDown();
        if (before !== this.input.value) this.input.dispatchEvent(new Event('change', { bubbles: true }));
      }));
    }
  });

  /* ---------- Dialogs ---------- */
  const openDialog = (dialog, opener) => {
    if (!dialog || dialog.open) return;
    dialog._opener = opener;
    if (typeof dialog.showModal === 'function') dialog.showModal(); else dialog.setAttribute('open', '');
  };
  document.addEventListener('click', (e) => {
    const opener = e.target.closest('[data-dialog-open]');
    if (opener) { e.preventDefault(); openDialog(document.getElementById(opener.dataset.dialogOpen), opener); return; }
    const closer = e.target.closest('[data-dialog-close]');
    if (closer) { closer.closest('dialog')?.close(); return; }
    if (e.target instanceof HTMLDialogElement && e.target.classList.contains('modal')) e.target.close();
  });
  document.addEventListener('close', (e) => { if (e.target instanceof HTMLDialogElement) e.target._opener?.focus?.(); }, true);
  N.openDialog = openDialog;

  /* ---------- Quick view ---------- */
  define('quick-view', class extends HTMLElement {
    connectedCallback() {
      this.dialog = this.querySelector('dialog');
      this.body = this.querySelector('[data-qv-body]');
      document.addEventListener('click', (e) => {
        const trigger = e.target.closest('[data-quick-view]');
        if (!trigger) return;
        e.preventDefault();
        this.open(trigger.dataset.quickView, trigger);
      });
      this.dialog?.addEventListener('close', () => { if (this.body) this.body.innerHTML = ''; });
    }
    async open(url, opener) {
      if (!this.dialog || !url) return;
      opener.classList.add('is-loading');
      try {
        const res = await fetch(`${url}${url.includes('?') ? '&' : '?'}section_id=quick-view`);
        if (!res.ok) throw new Error(String(res.status));
        const inner = parse(await res.text()).querySelector('[data-qv]');
        if (!inner) throw new Error('empty');
        this.body.replaceChildren(inner);
        this.hidden = false;
        document.querySelector('dialog[open]')?.close();
        openDialog(this.dialog, opener);
        paintWish(this.body);
        bus.emit('quickview:loaded', this.body);
      } catch (err) {
        window.location.href = url;
      } finally {
        opener.classList.remove('is-loading');
      }
    }
  });

  /* ---------- Wishlist ---------- */
  const WISH_KEY = 'sr-wishlist';
  const wish = {
    list() { const v = store.get(WISH_KEY, []); return Array.isArray(v) ? v : []; },
    save(list) { store.set(WISH_KEY, list.slice(0, 80)); paintWish(); bus.emit('wishlist:change', list); },
    toggle(handle) {
      const list = this.list();
      const i = list.indexOf(handle);
      if (i > -1) list.splice(i, 1); else list.unshift(handle);
      this.save(list);
      return i === -1;
    },
    remove(handles) { this.save(this.list().filter((h) => !handles.includes(h))); }
  };
  function paintWish(scope = document) {
    const list = wish.list();
    scope.querySelectorAll('[data-wish]').forEach((btn) => {
      const on = list.includes(btn.dataset.wish);
      btn.setAttribute('aria-pressed', String(on));
      const label = on ? btn.dataset.labelRemove : btn.dataset.labelAdd;
      if (label) btn.setAttribute('aria-label', label);
    });
    document.querySelectorAll('[data-wish-count]').forEach((el) => { el.textContent = list.length; el.hidden = !list.length; });
  }
  document.addEventListener('click', (e) => {
    const btn = e.target.closest('[data-wish]');
    if (!btn) return;
    e.preventDefault();
    e.stopPropagation();
    const added = wish.toggle(btn.dataset.wish);
    btn.classList.remove('is-pop');
    void btn.offsetWidth;
    if (added) btn.classList.add('is-pop');
    toast(added ? N.strings.wishAdded : N.strings.wishRemoved);
  });
  N.wishlist = wish;

  /* Product cards rendered by the theme's card-render section, for lists kept on the device */
  const loadCards = (handles) => Promise.all(handles.map(async (handle) => {
    try {
      const res = await fetch(`${N.routes.root}products/${encodeURIComponent(handle)}?section_id=card-render`);
      if (res.status === 404) return { handle, gone: true };
      if (!res.ok) return { handle };
      const card = parse(await res.text()).querySelector('[data-product-card]');
      return { handle, html: card ? card.outerHTML : null, gone: !card };
    } catch (err) { return { handle }; }
  }));

  const skeletons = (n) => Array.from({ length: n }, () => '<li aria-hidden="true"><div class="card card--skeleton"><div class="card__media"></div><span class="sk sk--line"></span><span class="sk sk--short"></span></div></li>').join('');
  N.skeletons = skeletons;

  define('wish-list', class extends HTMLElement {
    connectedCallback() {
      this.grid = this.querySelector('[data-wish-grid]');
      this.empty = this.querySelector('[data-wish-empty]');
      this.shareBox = this.querySelector('[data-wish-share]');
      this.sharedBox = this.querySelector('[data-wish-shared]');
      // A shared link (?envies=a,b,c) shows someone else's favourites without touching the visitor's own list.
      const shared = new URLSearchParams(window.location.search).get('envies') || '';
      this.shared = shared.split(',').map((h) => h.trim()).filter((h) => /^[\p{L}\p{N}_-]+$/u.test(h)).slice(0, 40);
      if (this.sharedBox) this.sharedBox.hidden = !this.shared.length;
      if (this.shared.length) {
        // Someone else's list: its heading becomes the page title, "my favourites" wording steps aside.
        const head = this.closest('.section')?.querySelector('.wishlist__head');
        const h1 = head?.querySelector('h1');
        const h2 = this.sharedBox?.querySelector('h2');
        if (h1 && h2) { h1.textContent = h2.textContent; h2.hidden = true; }
        head?.querySelector('.lead')?.setAttribute('hidden', '');
      }
      this.querySelector('[data-wish-share-btn]')?.addEventListener('click', () => this.share());
      this.querySelector('[data-wish-keep]')?.addEventListener('click', () => {
        const mine = wish.list();
        wish.save(this.shared.filter((h) => !mine.includes(h)).concat(mine));
        toast(this.dataset.kept);
      });
      this.render();
      document.addEventListener('sr:wishlist:change', () => { if (!this.shared.length) this.render(); });
    }
    async share() {
      const list = wish.list();
      if (!list.length) return;
      const url = `${window.location.origin}${window.location.pathname}?envies=${list.map(encodeURIComponent).join(',')}`;
      if (navigator.share) {
        try { await navigator.share({ title: this.dataset.shareTitle, text: this.dataset.shareText, url }); return; } catch (err) { if (err && err.name === 'AbortError') return; }
      }
      try { await navigator.clipboard.writeText(url); toast(this.dataset.copied); } catch (err) { window.prompt('', url); }
    }
    async render() {
      const list = this.shared.length ? this.shared : wish.list();
      if (!this.grid) return;
      if (this.shareBox) this.shareBox.hidden = Boolean(this.shared.length) || !list.length;
      if (!list.length) { this.grid.innerHTML = ''; if (this.empty) this.empty.hidden = false; return; }
      if (this.empty) this.empty.hidden = true;
      const ticket = (this.ticket = (this.ticket || 0) + 1);
      if (!this.grid.children.length) { this.grid.innerHTML = skeletons(Math.min(list.length, 8)); this.grid.setAttribute('aria-busy', 'true'); }
      const results = await loadCards(list);
      if (ticket !== this.ticket) return;
      this.grid.innerHTML = results.filter((r) => r.html).map((r) => `<li>${r.html}</li>`).join('');
      this.grid.setAttribute('aria-busy', 'false');
      paintWish(this.grid);
      bus.emit('cards:loaded', this.grid);
      const gone = results.filter((r) => r.gone).map((r) => r.handle);
      if (gone.length && !this.shared.length) wish.remove(gone);
    }
  });

  /* ---------- Recently viewed ---------- */
  const RECENT_KEY = 'sr-recent';
  define('recently-viewed', class extends HTMLElement {
    async connectedCallback() {
      const current = this.dataset.current || '';
      const limit = parseInt(this.dataset.limit || '4', 10);
      const list = store.get(RECENT_KEY, []).filter((h) => h !== current).slice(0, limit);
      const section = this.closest('.section');
      if (!list.length) { if (!window.Shopify?.designMode) section?.setAttribute('hidden', ''); return; }
      const grid = this.querySelector('[data-recent-grid]');
      if (grid && !grid.children.length) grid.innerHTML = skeletons(Math.min(list.length, limit));
      const results = await loadCards(list);
      const cards = results.filter((r) => r.html);
      if (!cards.length || !grid) { section?.setAttribute('hidden', ''); return; }
      grid.innerHTML = cards.map((r) => `<li>${r.html}</li>`).join('');
      section?.removeAttribute('hidden');
      paintWish(grid);
      bus.emit('cards:loaded', grid);
    }
  });
  const trackRecent = () => {
    document.querySelectorAll('[data-recent-handle]').forEach((el) => {
      const handle = el.dataset.recentHandle;
      if (!handle) return;
      const list = store.get(RECENT_KEY, []).filter((h) => h !== handle);
      list.unshift(handle);
      store.set(RECENT_KEY, list.slice(0, 16));
    });
  };

  /* ---------- Rails ---------- */
  /* ---------- Text that fills its line (footer name) ---------- */
  define('fit-text', class extends HTMLElement {
    connectedCallback() {
      this.inner = this.firstElementChild;
      if (!this.inner) return;
      this.lastWidth = 0;
      this.fit = () => {
        const width = this.clientWidth;
        if (!width || width === this.lastWidth) return;
        this.lastWidth = width;
        this.style.fontSize = '';
        const size = parseFloat(getComputedStyle(this).fontSize);
        const textWidth = this.inner.getBoundingClientRect().width;
        if (textWidth > 0) this.style.fontSize = `${Math.floor(size * (width / textWidth) * 995) / 1000}px`;
      };
      this.observer = new ResizeObserver(() => requestAnimationFrame(this.fit));
      this.observer.observe(this);
      document.fonts?.ready.then(() => { this.lastWidth = 0; this.fit(); });
    }
    disconnectedCallback() { this.observer?.disconnect(); }
  });

  /* ---------- Cookie preferences link (Shopify privacy banner) ---------- */
  const cookieLinks = () => {
    const banner = window.privacyBanner;
    if (!banner || typeof banner.showPreferences !== 'function') return false;
    document.querySelectorAll('[data-cookie-link]').forEach((li) => { li.hidden = false; });
    return true;
  };
  document.addEventListener('click', (e) => {
    if (e.target.closest('[data-cookie-prefs]')) window.privacyBanner?.showPreferences?.();
  });
  if (!cookieLinks()) {
    let tries = 0;
    const timer = setInterval(() => { if (cookieLinks() || ++tries > 20) clearInterval(timer); }, 500);
  }

  define('scroll-rail', class extends HTMLElement {
    connectedCallback() {
      this.track = this.querySelector('[data-track]');
      if (!this.track) return;
      this.prev = this.querySelector('[data-prev]');
      this.next = this.querySelector('[data-next]');
      const step = () => {
        const item = this.track.firstElementChild;
        return item ? item.getBoundingClientRect().width + parseFloat(getComputedStyle(this.track).columnGap || 20) : 320;
      };
      const behavior = () => (reduceMotion.matches ? 'auto' : 'smooth');
      this.prev?.addEventListener('click', () => this.track.scrollBy({ left: -step(), behavior: behavior() }));
      this.next?.addEventListener('click', () => this.track.scrollBy({ left: step(), behavior: behavior() }));
      this.update = () => {
        const max = this.track.scrollWidth - this.track.clientWidth - 2;
        if (this.prev) this.prev.disabled = this.track.scrollLeft <= 2;
        if (this.next) this.next.disabled = this.track.scrollLeft >= max;
        const nav = this.querySelector('.rail__nav');
        if (nav) nav.hidden = max <= 0;
      };
      this.track.addEventListener('scroll', debounce(this.update, 60), { passive: true });
      window.addEventListener('resize', debounce(this.update, 150));
      this.update();
    }
  });

  /* ---------- Tabs (WAI-ARIA) ---------- */
  define('tab-panels', class extends HTMLElement {
    connectedCallback() {
      this.tabs = [...this.querySelectorAll('[role="tab"]')].filter((t) => t.closest('tab-panels') === this);
      this.tabs.forEach((tab, i) => {
        tab.addEventListener('click', () => this.select(i));
        tab.addEventListener('keydown', (e) => {
          const keys = { ArrowRight: 1, ArrowLeft: -1, Home: 'first', End: 'last' };
          if (!(e.key in keys)) return;
          e.preventDefault();
          const k = keys[e.key];
          const n = k === 'first' ? 0 : k === 'last' ? this.tabs.length - 1 : (i + k + this.tabs.length) % this.tabs.length;
          this.select(n);
          this.tabs[n].focus();
        });
      });
    }
    select(index) {
      this.tabs.forEach((tab, i) => {
        const on = i === index;
        tab.setAttribute('aria-selected', String(on));
        tab.tabIndex = on ? 0 : -1;
        const panel = document.getElementById(tab.getAttribute('aria-controls'));
        if (panel) panel.hidden = !on;
      });
      const link = this.querySelector('[data-tab-link]');
      const active = this.tabs[index];
      if (link && active?.dataset.url) link.href = active.dataset.url;
    }
    selectPanel(panel) {
      const i = this.tabs.findIndex((t) => t.getAttribute('aria-controls') === panel.id);
      if (i > -1) this.select(i);
    }
  });

  /* ---------- Countdown ---------- */
  define('count-down', class extends HTMLElement {
    connectedCallback() {
      this.target = Date.parse(this.dataset.end || '');
      if (!this.target) return;
      this.parts = { d: this.querySelector('[data-days]'), h: this.querySelector('[data-hours]'), m: this.querySelector('[data-minutes]'), s: this.querySelector('[data-seconds]') };
      this.tick();
      this.timer = setInterval(() => this.tick(), 1000);
    }
    disconnectedCallback() { clearInterval(this.timer); }
    tick() {
      const left = this.target - Date.now();
      if (left <= 0) {
        clearInterval(this.timer);
        if (this.dataset.hideWhenDone === 'true') this.closest('[data-countdown-host]')?.setAttribute('hidden', '');
        else if (this.dataset.done) { this.textContent = this.dataset.done; this.classList.add('is-done'); }
        return;
      }
      const pad = (n) => String(n).padStart(2, '0');
      const s = Math.floor(left / 1000);
      if (this.parts.d) this.parts.d.textContent = pad(Math.floor(s / 86400));
      if (this.parts.h) this.parts.h.textContent = pad(Math.floor((s % 86400) / 3600));
      if (this.parts.m) this.parts.m.textContent = pad(Math.floor((s % 3600) / 60));
      if (this.parts.s) this.parts.s.textContent = pad(s % 60);
    }
  });

  /* ---------- Welcome popup ---------- */
  define('welcome-popup', class extends HTMLElement {
    connectedCallback() {
      this.dialog = this.querySelector('dialog');
      if (!this.dialog) return;
      const key = this.dataset.key || 'sr-welcome';
      const seen = parseInt(store.get(key, 0), 10) || 0;
      const days = parseInt(this.dataset.days || '30', 10);
      if (seen && Date.now() - seen < days * 86400000) return;
      const remember = () => store.set(key, Date.now());
      this.dialog.addEventListener('close', remember);
      this.querySelector('[data-welcome-accept]')?.addEventListener('click', remember);
      const copy = this.querySelector('[data-copy-code]');
      copy?.addEventListener('click', async () => {
        const code = copy.dataset.copyCode;
        try { await navigator.clipboard.writeText(code); } catch (err) { /* manual copy */ }
        const label = copy.querySelector('[data-copy-label]');
        if (label) label.textContent = copy.dataset.copied || code;
        copy.classList.add('is-copied');
      });
      afterAge(() => setTimeout(() => {
        if (document.querySelector('dialog[open]') || document.body.classList.contains('drawer-open') || ageLocked()) return;
        openDialog(this.dialog);
      }, Math.max(1, parseInt(this.dataset.delay || '6', 10)) * 1000));
    }
  });

  /* ---------- WhatsApp tab ---------- */
  define('whatsapp-tab', class extends HTMLElement {
    connectedCallback() {
      const delay = Math.max(0, parseInt(this.dataset.delay || '0', 10)) * 1000;
      afterAge(() => setTimeout(() => this.classList.add('is-in'), reduceMotion.matches ? 0 : delay));
      this.toggle = this.querySelector('[data-wa-toggle]');
      this.card = this.querySelector('.wa__card');
      if (!this.toggle || !this.card) return;
      this.toggle.addEventListener('click', () => (this.card.hidden ? this.open() : this.close()));
      this.querySelector('[data-wa-close]')?.addEventListener('click', () => this.close());
      this.addEventListener('keydown', (e) => { if (e.key === 'Escape') this.close(); });
      document.addEventListener('click', (e) => { if (!this.contains(e.target)) this.close(false); });
    }
    open() {
      this.card.hidden = false;
      this.toggle.setAttribute('aria-expanded', 'true');
      requestAnimationFrame(() => this.card.classList.add('is-open'));
      this.querySelector('[data-wa-link]')?.focus({ preventScroll: true });
    }
    close(refocus = true) {
      if (this.card.hidden) return;
      this.card.classList.remove('is-open');
      this.toggle.setAttribute('aria-expanded', 'false');
      setTimeout(() => { this.card.hidden = true; }, 250);
      if (refocus) this.toggle.focus({ preventScroll: true });
    }
  });

  /* ---------- Delivery estimate with a live dispatch countdown (shop time zone) ---------- */
  define('delivery-estimate', class extends HTMLElement {
    connectedCallback() {
      this.render();
      this.hidden = this.hasAttribute('data-unavailable');
      if (!this.timer) this.timer = setInterval(() => this.render(), 30000);
    }
    disconnectedCallback() { clearInterval(this.timer); this.timer = null; }
    shopNow() {
      const now = new Date();
      const m = /^([+-])(\d{2})(\d{2})$/.exec(this.dataset.offset || '');
      if (!m) return now;
      const offset = (m[1] === '-' ? -1 : 1) * (parseInt(m[2], 10) * 60 + parseInt(m[3], 10));
      return new Date(now.getTime() + (offset + now.getTimezoneOffset()) * 60000);
    }
    render() {
      const d = this.dataset;
      const skip = d.skipWeekends === 'true';
      const now = this.shopNow();
      const cutoff = parseInt(d.cutoff, 10);
      const processing = parseInt(d.processing, 10) || 0;
      const isOff = (date) => skip && (date.getDay() === 0 || date.getDay() === 6);
      const addBusinessDays = (date, days) => {
        const out = new Date(date);
        let left = days;
        while (isOff(out)) out.setDate(out.getDate() + 1);
        while (left > 0) {
          out.setDate(out.getDate() + 1);
          if (!isOff(out)) left -= 1;
        }
        return out;
      };
      const late = now.getHours() >= cutoff;
      const ship = addBusinessDays(new Date(now), processing + (late ? 1 : 0));
      const from = addBusinessDays(ship, parseInt(d.min, 10));
      const to = addBusinessDays(ship, Math.max(parseInt(d.max, 10), parseInt(d.min, 10)));
      let fmt;
      try { fmt = new Intl.DateTimeFormat(d.locale || undefined, { weekday: 'long', day: 'numeric', month: 'long' }); }
      catch (err) { fmt = new Intl.DateTimeFormat(undefined, { weekday: 'long', day: 'numeric', month: 'long' }); }
      const text = this.querySelector('[data-delivery-text]');
      if (text) text.textContent = (d.text || '').replace('[from]', fmt.format(from)).replace('[to]', fmt.format(to)) + '.';
      const cut = this.querySelector('[data-delivery-cutoff]');
      if (!cut) return;
      // Before the cut-off on a shipping day: live countdown to the cut-off.
      const live = !late && !isOff(now);
      cut.classList.toggle('is-live', live);
      if (live) {
        const end = new Date(now);
        end.setHours(cutoff, 0, 0, 0);
        const mins = Math.max(1, Math.ceil((end - now) / 60000));
        const h = Math.floor(mins / 60);
        const m = mins % 60;
        const strong = document.createElement('strong');
        strong.textContent = h > 0 ? `${h} h ${String(m).padStart(2, '0')}` : `${m} min`;
        const midnight = (date) => new Date(date.getFullYear(), date.getMonth(), date.getDate()).getTime();
        const days = Math.round((midnight(ship) - midnight(now)) / 864e5);
        const when = days <= 0 ? d.today : days === 1 ? d.tomorrow : (d.onDay || '[day]').replace('[day]', fmt.format(ship));
        const [before, after = ''] = (d.cutoffText || '[time]').split('[time]');
        cut.replaceChildren(document.createTextNode(before.replace('[day]', when)), strong, document.createTextNode(after.replace('[day]', when)));
      } else {
        cut.textContent = d.shipsText ? d.shipsText.replace('[day]', fmt.format(ship)) : '';
      }
      cut.hidden = !cut.textContent.trim();
    }
  });

  /* ---------- Share ---------- */
  define('share-button', class extends HTMLElement {
    connectedCallback() {
      this.querySelector('button')?.addEventListener('click', async () => {
        const url = this.dataset.url || window.location.href;
        if (navigator.share) {
          try { await navigator.share({ title: this.dataset.title, url }); } catch (err) { /* dismissed */ }
          return;
        }
        try { await navigator.clipboard.writeText(url); toast(this.dataset.copied || url); }
        catch (err) { window.prompt('', url); }
      });
    }
  });

  /* ---------- Rating: mirror the page's review summary when no app rating ---------- */
  const syncRatings = (scope = document) => {
    const summary = document.querySelector('[data-reviews-summary]');
    if (!summary) return;
    scope.querySelectorAll('[data-rating-block][hidden]').forEach((block) => {
      const rating = parseFloat(summary.dataset.rating);
      const count = parseInt(summary.dataset.count, 10);
      if (!rating || !count) return;
      const stars = block.querySelector('.stars');
      if (stars) {
        stars.style.setProperty('--fill', `${Math.min(rating / 5, 1) * 100}%`);
        stars.setAttribute('aria-label', `${summary.dataset.label} / 5`);
      }
      const text = block.querySelector('[data-rating-text]');
      const label = summary.querySelector('.reviews__total')?.textContent.trim();
      if (text) text.innerHTML = `${summary.dataset.label}${block.dataset.showCount === 'true' && label ? ` <span>(${label})</span>` : ''}`;
      block.hidden = false;
    });
  };

  /* ---------- Cart terms ---------- */
  document.addEventListener('invalid', (e) => {
    if (e.target.matches?.('[data-cart-terms]')) e.target.closest('.summary__terms')?.classList.add('is-error');
  }, true);
  document.addEventListener('change', (e) => {
    if (e.target.matches?.('[data-cart-terms]') && e.target.checked) e.target.closest('.summary__terms')?.classList.remove('is-error');
  });

  /* ---------- Misc ---------- */
  document.addEventListener('change', (e) => {
    if (e.target.matches('select[data-autosubmit]')) e.target.form?.submit();
  });
  document.addEventListener('click', (e) => {
    const toggle = e.target.closest('[data-toggle-recover]');
    if (!toggle) return;
    e.preventDefault();
    document.getElementById('Recover')?.toggleAttribute('hidden');
    document.getElementById('Login')?.toggleAttribute('hidden');
  });
  document.addEventListener('submit', (e) => {
    const message = e.target.dataset?.confirm;
    if (message && !window.confirm(message)) e.preventDefault();
  });

  const init = (scope = document) => {
    syncRatings(scope);
    paintWish(scope);
    scope.querySelectorAll('select[data-default]').forEach((s) => { if (s.dataset.default) s.value = s.dataset.default; });
    if (window.matchMedia('(max-width: 749px)').matches) scope.querySelectorAll('.footer__menu[open]').forEach((d) => d.removeAttribute('open'));
  };
  if (window.location.hash === '#recover') {
    document.getElementById('Recover')?.removeAttribute('hidden');
    document.getElementById('Login')?.setAttribute('hidden', '');
  }
  init();
  trackRecent();
  document.addEventListener('shopify:section:load', (e) => init(e.target));
  document.addEventListener('sr:cards:loaded', (e) => paintWish(e.detail instanceof Element ? e.detail : document));
  document.addEventListener('shopify:section:select', (e) => {
    const drawer = e.target.querySelector('cart-drawer');
    if (drawer) drawer.open();
  });
  document.addEventListener('shopify:section:deselect', (e) => e.target.querySelector('cart-drawer')?.close());
})();
